import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { requireRowsWritten } from "@/lib/rowsWritten";

// ── Stores ──

export function fetchStoresByOwner(userId: string) {
  return supabase
    .from("stores")
    .select("id, name, subscription_status, creation_source")
    .eq("owner_id", userId)
    .order("created_at", { ascending: true });
}

export function fetchStoreById(storeId: string) {
  return supabase
    .from("stores")
    .select("*")
    .eq("id", storeId)
    .single();
}

/** Minimal store row for shell context (e.g. support session target). */
export function fetchStoreSummaryById(storeId: string) {
  return supabase
    .from("stores")
    .select("id, name, subscription_status, creation_source")
    .eq("id", storeId)
    .maybeSingle();
}

export function fetchStoreName(storeId: string) {
  return supabase
    .from("stores")
    .select("name")
    .eq("id", storeId)
    .single();
}

export function fetchStoreSubscription(storeId: string) {
  return supabase
    .from("stores")
    .select(
      "subscription_status, creation_source, trial_ends_at, trial_extended, grace_ends_at, archived_at, deletion_scheduled_at, past_due_since, cancel_at_period_end, cancel_at",
    )
    .eq("id", storeId)
    .single();
}

/** Aucune ligne modifiée (refus RLS) renvoie une erreur : voir `requireRowsWritten`. */
export function updateStore(storeId: string, data: TablesUpdate<"stores">) {
  return requireRowsWritten(
    supabase
      .from("stores")
      .update(data)
      .eq("id", storeId)
      .select("id"),
    "Vous n'avez pas les droits pour modifier les informations de ce magasin.",
  );
}

export function deleteStore(storeId: string) {
  return supabase
    .from("stores")
    .delete()
    .eq("id", storeId);
}

/** @deprecated Franchisees must use createManualStore (RLS blocks direct INSERT). */
export function insertStore(data: TablesInsert<"stores">) {
  return supabase.from("stores").insert(data).select("id").single();
}

/** Creates a non-signup store: pending_subscription, no trial. */
export function createManualStore(params: {
  name: string;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  phone?: string | null;
}) {
  return supabase.rpc("create_manual_store", {
    p_name: params.name,
    p_address: params.address ?? undefined,
    p_postal_code: params.postal_code ?? undefined,
    p_city: params.city ?? undefined,
    p_phone: params.phone ?? undefined,
  });
}

export function fetchStoreOpeningHours(storeId: string) {
  return supabase
    .from("stores")
    .select("opening_hours")
    .eq("id", storeId)
    .single();
}

export function fetchStoreForExport(storeId: string) {
  return supabase
    .from("stores")
    .select("name, address, city, postal_code, siret")
    .eq("id", storeId)
    .single();
}

export function fetchStorePlanningInfo(storeId: string) {
  return supabase
    .from("stores")
    .select("owner_id, opening_hours")
    .eq("id", storeId)
    .single();
}

export function fetchStoreDetails(storeId: string) {
  return supabase
    .from("stores")
    .select("id, name, address, city, postal_code, phone, company_name, legal_form, capital_amount, siret, rcs_city, company_address, company_postal_code, company_city, opening_hours")
    .eq("id", storeId)
    .maybeSingle();
}

export function fetchStoreBillingFields(storeId: string) {
  return supabase
    .from("stores")
    .select(
      "company_name, siret, vat_number, billing_address, billing_city, billing_postal_code, company_address, company_city, company_postal_code"
    )
    .eq("id", storeId)
    .single();
}

export function fetchStoreSettingsHolidays(storeId: string) {
  return supabase
    .from("store_settings")
    .select("holidays")
    .eq("store_id", storeId)
    .maybeSingle();
}

export function fetchStoreSettingsBreaks(storeId: string) {
  return supabase
    .from("store_settings")
    .select("break_duration_minutes, break_after_hours, holidays, paid_leave_counts_as_effective_work")
    .eq("store_id", storeId)
    .maybeSingle();
}

export function fetchStoreSettingsForPlanning(storeId: string) {
  return supabase
    .from("store_settings")
    .select("break_duration_minutes, break_after_hours, break_is_paid, owner_in_planning, owner_planning_mode")
    .eq("store_id", storeId)
    .maybeSingle();
}

export function fetchStoreSettingsMealBreak(storeId: string) {
  return supabase
    .from("store_settings")
    .select("meal_break_premium_amount")
    .eq("store_id", storeId)
    .maybeSingle();
}

// ── Store settings ──

export function fetchStoreSettings(storeId: string) {
  return supabase
    .from("store_settings")
    .select("*")
    .eq("store_id", storeId)
    .maybeSingle();
}

export function upsertStoreSettings(
  storeId: string,
  data: TablesUpdate<"store_settings">,
) {
  return supabase
    .from("store_settings")
    .upsert({ store_id: storeId, ...data }, { onConflict: "store_id" });
}

export function updateStoreSettings(
  storeId: string,
  data: TablesUpdate<"store_settings">,
) {
  return supabase
    .from("store_settings")
    .update(data)
    .eq("store_id", storeId);
}

export function updateStoreSettingsById(
  settingsId: string,
  data: TablesUpdate<"store_settings">,
) {
  return supabase
    .from("store_settings")
    .update(data)
    .eq("id", settingsId)
    .select("*")
    .maybeSingle();
}

export function insertStoreSettings(data: TablesInsert<"store_settings">) {
  return supabase.from("store_settings").insert(data).select("*").single();
}

export function fetchStoreFields(storeId: string, select: string) {
  return supabase
    .from("stores")
    .select(select as "*")
    .eq("id", storeId)
    .single();
}

export function fetchStoreSettingsFields(storeId: string, select: string) {
  return supabase
    .from("store_settings")
    .select(select as "*")
    .eq("store_id", storeId)
    .maybeSingle();
}

// ── Store inventories ──

export function fetchStoreInventories(
  storeId: string,
  dateGte: string,
  dateLte: string,
) {
  return supabase
    .from("store_inventories")
    .select("*")
    .eq("store_id", storeId)
    .gte("date", dateGte)
    .lte("date", dateLte)
    .order("date", { ascending: true });
}

export function insertStoreInventory(data: {
  store_id: string;
  date: string;
  start_time: string;
  end_time: string;
}) {
  return supabase.from("store_inventories").insert(data);
}

export function deleteStoreInventory(inventoryId: string) {
  return requireRowsWritten(
    supabase
      .from("store_inventories")
      .delete()
      .eq("id", inventoryId)
      .select("id"),
    "Vous n'avez pas les droits pour supprimer cet inventaire, ou il a déjà été supprimé.",
  );
}
